<template>
  <base-drawer :show="show" title="API request" width="520px" @close="onClose">
    <template v-if="request">
      <drawer-section title="Request" icon="icon-send">
        <div class="drawer-detail-row text-white">
          <span>Status</span>
          <span class="tag-chip text-white" :style="tagChipStyle(statusColor)">{{ request.status ? request.status + ' ' + statusLabel : statusLabel }}</span>
        </div>
        <div class="drawer-detail-row text-white">
          <span>Method</span>
          <span>{{ request.method }}</span>
        </div>
        <div class="drawer-detail-row text-white">
          <span>Endpoint</span>
          <span class="text-right text-break ml-3">{{ request.path }}</span>
        </div>
        <div class="drawer-detail-row text-white">
          <span>Time</span>
          <span>{{ timeText }}</span>
        </div>
        <div class="drawer-detail-row text-white">
          <span>Duration</span>
          <span v-if="request.duration_ms !== null">{{ request.duration_ms }} ms</span>
          <span v-else>Not recorded</span>
        </div>
        <div v-if="request.error_message" class="drawer-detail-row text-white">
          <span>Error</span>
          <span class="text-right text-break ml-3">{{ request.error_message }}</span>
        </div>
        <label class="mt-3">Request ID</label>
        <div v-if="request.request_id" class="credential-field">
          <code>{{ request.request_id }}</code>
          <i class="tim-icons icon-single-copy-04" title="Copy" @click="copy(request.request_id)"></i>
        </div>
        <p v-else class="text-white mb-0">Not recorded</p>
      </drawer-section>

      <drawer-section title="Caller" icon="icon-world">
        <div class="drawer-detail-row text-white">
          <span>IP address</span>
          <span>{{ request.ip || 'Not recorded' }}</span>
        </div>
        <div class="drawer-detail-row text-white">
          <span>User agent</span>
          <span class="text-right text-break ml-3">{{ request.user_agent || 'Not recorded' }}</span>
        </div>
        <div class="drawer-detail-row text-white">
          <span>API key ID</span>
          <span>{{ keyLabel }}</span>
        </div>
      </drawer-section>

      <drawer-section title="Query parameters" icon="icon-bullet-list-67">
        <template v-if="paramKeys.length">
          <div v-for="k in paramKeys" :key="k" class="drawer-detail-row text-white">
            <span>{{ k }}</span>
            <span class="text-right text-break ml-3">{{ params[k] }}</span>
          </div>
        </template>
        <p v-else class="text-white mb-0">{{ request.source === 'backfill' && !request.request_id ? 'Not recorded' : 'None' }}</p>
      </drawer-section>

      <drawer-section v-if="request.has_request_body" title="Request body" icon="icon-upload">
        <p v-if="bodiesLoading" class="text-white mb-0"><i class="el-icon-loading"></i> Loading</p>
        <pre v-else class="mb-0">{{ requestBodyText }}</pre>
      </drawer-section>

      <drawer-section v-if="request.has_response_body" title="Response body" icon="icon-cloud-download-93">
        <p v-if="bodiesLoading" class="text-white mb-0"><i class="el-icon-loading"></i> Loading</p>
        <pre v-else class="mb-0">{{ responseBodyText }}</pre>
      </drawer-section>
    </template>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Close</base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
// Read-only detail of one Community API request (a row of api_request_log).
import { computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { tagChipStyle } from '@/util/tagChip'
import { statusClass, STATUS_LABELS, STATUS_COLORS, parseQueryParams, formatBody } from '@/util/apiRequestLog'

const props = defineProps({
  show: { type: Boolean, default: false },
  request: { type: Object as () => Record<string, any> | null, default: null },
  // True while the page is fetching this row's request and response bodies.
  bodiesLoading: { type: Boolean, default: false },
  // Every key the company has had: [{ id, api_id, status }]
  keys: { type: Array as () => Array<Record<string, any>>, default: () => [] },
})

const emit = defineEmits(['update:show', 'close'])

const { proxy } = getCurrentInstance() as any

const statusKind = computed(() => statusClass(props.request ? props.request.status : null))
const statusLabel = computed(() => STATUS_LABELS[statusKind.value])
const statusColor = computed(() => STATUS_COLORS[statusKind.value])

const timeText = computed(() => {
  if (!props.request) return ''
  return formatGroupDate(props.request.created_at) + ', ' + formatGroupTime(props.request.created_at)
})

const params = computed(() => parseQueryParams(props.request ? props.request.query_params : null))
const paramKeys = computed(() => Object.keys(params.value))

// Bodies arrive masked from the server: customer and card fields are starred before they are stored.
const requestBodyText = computed(() => formatBody(props.request ? props.request.request_body : null))
const responseBodyText = computed(() => formatBody(props.request ? props.request.response_body : null))

const keyLabel = computed(() => {
  const id = props.request ? props.request.api_key_id : null
  if (id === null || id === undefined) return 'Not recorded'
  const key = props.keys.find(k => Number(k.id) === Number(id))
  if (!key) return 'Not recorded'
  return key.api_id + (Number(key.status) === 1 ? '' : ' (rolled)')
})

function copy(text: string) {
  navigator.clipboard.writeText(text)
  proxy.$notify({ type: 'success', message: 'Copied to clipboard', icon: 'tim-icons icon-check-2' })
}

function onClose() {
  emit('update:show', false)
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) document.addEventListener('keydown', onEscKey)
  else document.removeEventListener('keydown', onEscKey)
}, { immediate: true })

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<script lang="ts">
export default { name: 'ApiRequestDrawer' }
</script>
